import type { RealtimeChannel, SupabaseClient } from '@supabase/supabase-js'
import {
  getSupabaseClient,
  subscribeToSupabaseRealtimeHeartbeat,
  type SupabaseRealtimeHeartbeatStatus,
} from '../supabase/client'
import type { SyncDomain } from './syncRuntime'

export type SyncRealtimeStatus = 'connected' | 'disconnected'

export const SYNC_INVALIDATION_TOPIC_VERSION = 2

interface BroadcastChangePayload {
  version?: number
  domain?: unknown
  table?: string
  is_dev?: boolean
}

type SubscribeHeartbeat = (
  listener: (status: SupabaseRealtimeHeartbeatStatus, latency?: number) => void,
) => () => void

function domainForTable(table: string | undefined): SyncDomain | null {
  if (table === 'notes') return 'notes'
  if (table === 'settings') return 'settings'
  if (table === 'note_assets') return 'assets'
  return null
}

function normalizeDomain(domain: unknown): SyncDomain | null {
  if (
    domain === 'notes'
    || domain === 'settings'
    || domain === 'assets'
  ) return domain
  return null
}

export function syncInvalidationTopic(userId: string, isDev: boolean): string {
  const environment = isDev ? 'development' : 'production'
  return `minimany-sync:${environment}:${userId}`
}

export async function subscribeToSyncInvalidations(
  userId: string,
  isDev: boolean,
  onInvalidate: (domain: SyncDomain) => void,
  onStatus: (status: SyncRealtimeStatus) => void,
  loadClient: () => Promise<SupabaseClient> = getSupabaseClient,
  subscribeHeartbeat: SubscribeHeartbeat = subscribeToSupabaseRealtimeHeartbeat,
): Promise<() => Promise<void>> {
  const client = await loadClient()
  await client.realtime.setAuth()
  let channel: RealtimeChannel | null = client.channel(syncInvalidationTopic(userId, isDev), {
    config: { private: true },
  })
  const unsubscribeHeartbeat = subscribeHeartbeat((status) => {
    if (status === 'timeout' || status === 'error') onStatus('disconnected')
  })
  const handle = (message: { payload?: BroadcastChangePayload }) => {
    const payload = message.payload
    const domain = payload?.version === SYNC_INVALIDATION_TOPIC_VERSION
      ? normalizeDomain(payload.domain)
      : domainForTable(payload?.table)
    if (domain && payload?.is_dev === isDev) onInvalidate(domain)
  }
  for (const event of ['INSERT', 'UPDATE', 'DELETE']) {
    channel = channel.on('broadcast', { event }, handle)
  }
  channel.subscribe((status) => {
    onStatus(status === 'SUBSCRIBED' ? 'connected' : 'disconnected')
  })
  return async () => {
    unsubscribeHeartbeat()
    if (!channel) return
    await client.removeChannel(channel)
    channel = null
  }
}
